import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { MultiSelect } from '../../../components/ui/multi-select';
import {
  useCreateGroup,
  useUpdateGroup,
  useDeskStatuses,
  useDeskRequestTypes,
  useGateTransitions,
} from '../../../hooks/useValidationGroups';
import type {
  ValidationGroup,
  DecisionRecipient,
  DecisionKind,
  GateTransition,
} from '../../../api/validationGroupsApi';

// Radix treats '' as "no selection", so the reset entry needs a real value.
const ALL_REQUEST_TYPES = '__all__';

interface ServiceDeskOption {
  id: number;
  name: string;
}

// UI kinds are keyed by transition id; the API stores them keyed by the transition's
// target status id (what the webhook sees). These two helpers convert both ways.
const kindsToApi = (
  byTransition: Record<string, DecisionKind>,
  transitions: GateTransition[]
): Record<string, DecisionKind> => {
  const byStatus: Record<string, DecisionKind> = {};
  for (const tr of transitions) {
    const kind = byTransition[tr.id];
    if (kind && tr.toStatusId) byStatus[tr.toStatusId] = kind;
  }
  return byStatus;
};

const kindsFromApi = (
  byStatus: Record<string, DecisionKind>,
  transitions: GateTransition[]
): Record<string, DecisionKind> => {
  const byTransition: Record<string, DecisionKind> = {};
  for (const tr of transitions) {
    const kind = tr.toStatusId ? byStatus[tr.toStatusId] : undefined;
    if (kind) byTransition[tr.id] = kind;
  }
  return byTransition;
};

interface GroupFormDialogProps {
  open: boolean;
  group: ValidationGroup | null; // null = create
  serviceDesks: ServiceDeskOption[];
  onClose: () => void;
}

export const GroupFormDialog = ({ open, group, serviceDesks, onClose }: GroupFormDialogProps) => {
  const { t } = useTranslation();
  const isEdit = group != null;

  // Options built from literal translation keys — the i18n static guard rejects variables.
  const recipientOptions: { value: DecisionRecipient; label: string }[] = [
    { value: 'assignee', label: t('Ticket assignee') },
    { value: 'reporter', label: t('Request reporter') },
    { value: 'group_members', label: t('Group members') },
  ];

  const [serviceDeskId, setServiceDeskId] = useState('');
  // UI-only filter: the gate is still (desk, status), but a desk exposes every status of
  // every workflow, so picking a request type narrows the list to its own workflow.
  const [requestTypeId, setRequestTypeId] = useState('');
  const [statusId, setStatusId] = useState('');
  const [name, setName] = useState('');
  // Jira transition ids offered at this gate; empty = all transitions offered.
  const [allowed, setAllowed] = useState<string[]>([]);
  // Who receives the decision email when a ticket leaves this gate.
  const [recipients, setRecipients] = useState<DecisionRecipient[]>(['assignee']);
  // Decision wording per transition (UI keyed by transition id, stored keyed by target status).
  const [kinds, setKinds] = useState<Record<string, DecisionKind>>({});
  // The kinds are hydrated from the saved group once the transitions land; a later refetch
  // (window focus, staleTime expiry) must not clobber edits in progress.
  const kindsHydrated = useRef(false);

  const create = useCreateGroup();
  const update = useUpdateGroup();
  const mutation = isEdit ? update : create;

  const deskId = serviceDeskId ? Number(serviceDeskId) : null;
  const { data: statuses, isLoading: statusesLoading, error: statusesError } = useDeskStatuses(deskId);
  const { data: requestTypes } = useDeskRequestTypes(deskId);

  const gateStatusId = statusId ? Number(statusId) : null;
  const {
    data: transitions,
    isLoading: transitionsLoading,
    isSuccess: transitionsLoaded,
  } = useGateTransitions(deskId, gateStatusId);
  // Only a query that actually ran and came back empty means Jira refused the workflow API.
  // A query still disabled (no status picked yet) is neither loading nor loaded — never a failure.
  const transitionsUnavailable = transitionsLoaded && transitions.length === 0;

  // Reset the form each time the dialog opens (with the edited group's values, if any).
  useEffect(() => {
    if (!open) return;
    setServiceDeskId(group ? String(group.service_desk_id) : '');
    setRequestTypeId('');
    setStatusId(group ? String(group.status_id) : '');
    setName(group?.name ?? '');
    setAllowed(group?.allowed_transitions ?? []);
    setRecipients(group?.decision_recipients ?? ['assignee']);
    setKinds({});
    kindsHydrated.current = false;
  }, [open, group]);

  // The stored kinds are keyed by target status: they only map back to transitions
  // once the discovery query returns them.
  useEffect(() => {
    if (!open || !group || !transitions?.length || kindsHydrated.current) return;
    kindsHydrated.current = true;
    setKinds(kindsFromApi(group.decision_kinds ?? {}, transitions));
  }, [open, group, transitions]);

  // Transitions the admin can qualify: the offered ones (allowlist, or all when empty),
  // restricted to those whose target status is known.
  const qualifiable = (transitions ?? []).filter(
    (tr) => tr.toStatusId && (allowed.length === 0 || allowed.includes(tr.id))
  );

  const issueTypeId = requestTypes?.find((rt) => rt.id === requestTypeId)?.issueTypeId;

  // The statuses come from a slow Jira call: until it answers, a provisional entry keeps
  // the edited group's current status visible instead of an empty select.
  const statusOptions = (
    statuses ??
    (isEdit && statusId === String(group.status_id) ? [{ id: statusId, name: '…', issueTypeIds: [] }] : [])
  ).filter((s) => !issueTypeId || s.issueTypeIds.includes(issueTypeId));

  // A request type that doesn't cover the selected status leaves the select showing a value
  // no longer in its options — clear it rather than submit a status the admin can't see.
  const handleRequestTypeChange = (value: string) => {
    setRequestTypeId(value);
    const next = requestTypes?.find((rt) => rt.id === value)?.issueTypeId;
    const stillVisible = (statuses ?? []).some(
      (s) => s.id === statusId && (!next || s.issueTypeIds.includes(next))
    );
    if (!stillVisible) setStatusId('');
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!serviceDeskId || !statusId || !name.trim()) return;

    const payload = {
      serviceDeskId: Number(serviceDeskId),
      statusId: Number(statusId),
      name: name.trim(),
      allowedTransitions: allowed,
      decisionRecipients: recipients,
      decisionKinds: kindsToApi(kinds, transitions ?? []),
    };

    if (isEdit) {
      update.mutate({ id: group.id, data: payload }, { onSuccess: onClose });
    } else {
      create.mutate(payload, { onSuccess: onClose });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? t('Edit group') : t('New group')}</DialogTitle>
          <DialogDescription>{t('A group grants its members the right to validate tickets at a given status.')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="grp-name">{t('Name')}</Label>
            <Input id="grp-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div>
            <Label htmlFor="grp-desk">{t('Service desk')}</Label>
            {/* Inside a form, Radix syncs a hidden native select whose options are not mounted
                while closed: setting the controlled value programmatically re-fires
                onValueChange with a spurious empty value. Ignore anything but a real change. */}
            <Select
              value={serviceDeskId}
              onValueChange={(v) => {
                if (!v || v === serviceDeskId) return;
                setServiceDeskId(v);
                setRequestTypeId('');
                setStatusId('');
              }}
            >
              <SelectTrigger id="grp-desk"><SelectValue placeholder={t('Choose a service desk')} /></SelectTrigger>
              <SelectContent>
                {serviceDesks.map((sd) => (
                  <SelectItem key={sd.id} value={String(sd.id)}>{sd.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="grp-request-type">{t('Request type')}</Label>
            <Select
              value={requestTypeId || undefined}
              onValueChange={(v) => v && handleRequestTypeChange(v === ALL_REQUEST_TYPES ? '' : v)}
              disabled={!deskId || !requestTypes?.length}
            >
              <SelectTrigger id="grp-request-type">
                <SelectValue placeholder={t('All request types')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_REQUEST_TYPES}>{t('All request types')}</SelectItem>
                {(requestTypes ?? []).map((rt) => (
                  <SelectItem key={rt.id} value={rt.id}>{rt.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-gray-500">
              {t('Narrows the statuses below to the workflow of that request type. Not saved on the group.')}
            </p>
          </div>

          <div>
            <Label htmlFor="grp-status">{t('Status')}</Label>
            {/* `undefined` (not '') when unset: Radix latches the empty string as a real
                selection and then keeps showing the placeholder after the value arrives. */}
            {/* Same spurious-empty guard as the desk select above. */}
            <Select
              value={statusId || undefined}
              onValueChange={(v) => v && setStatusId(v)}
              disabled={!deskId || statusesLoading}
            >
              <SelectTrigger id="grp-status">
                {/* Explicit label: Radix cannot resolve a controlled value's text while the
                    items are not mounted yet (disabled select during the statuses fetch). */}
                <SelectValue placeholder={statusesLoading ? t('Loading…') : t('Choose a status')}>
                  {statusId
                    ? (() => {
                        const current = statusOptions.find((s) => s.id === statusId);
                        return current ? `${current.name} (${current.id})` : `(${statusId})`;
                      })()
                    : null}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name} ({s.id})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Without this, a refused desk just yields an empty dropdown with no explanation. */}
            {statusesError ? (
              <p className="mt-1 text-xs text-red-600">{(statusesError as Error).message}</p>
            ) : null}
          </div>

          <div>
            <Label htmlFor="grp-transitions">{t('Allowed transitions')}</Label>
            {transitionsUnavailable ? (
              <>
                <Input
                  id="grp-transitions"
                  value={allowed.join(', ')}
                  onChange={(e) => setAllowed(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
                  placeholder={t('e.g. 21, 31 — empty = all')}
                />
                <p className="mt-1 text-xs text-amber-600">
                  {t('Jira did not return the workflow transitions. Enter the transition ids, separated by commas.')}
                </p>
              </>
            ) : (
              <>
                <MultiSelect
                  options={(transitions ?? []).map((tr) => ({ value: tr.id, label: `${tr.name} (${tr.id})` }))}
                  selected={allowed}
                  onChange={setAllowed}
                  disabled={!gateStatusId || transitionsLoading}
                  placeholder={
                    !gateStatusId
                      ? t('Choose a status')
                      : transitionsLoading
                        ? t('Loading…')
                        : t('All transitions')
                  }
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t('Leave empty to offer all transitions available at this status.')}
                </p>
              </>
            )}
          </div>

          <div>
            <Label htmlFor="grp-recipients">{t('Decision email recipients')}</Label>
            <MultiSelect
              options={recipientOptions}
              selected={recipients}
              onChange={(values) => setRecipients(values as DecisionRecipient[])}
              placeholder={t('Nobody')}
            />
            <p className="mt-1 text-xs text-gray-500">
              {t('Who receives the email once the ticket leaves this gate (validated or rejected).')}
            </p>
          </div>

          {transitionsLoading && gateStatusId ? (
            <div>
              <Label>{t('Transition outcomes')}</Label>
              <p className="mt-1 text-xs text-gray-500">{t('Loading…')}</p>
            </div>
          ) : qualifiable.length > 0 ? (
            <div>
              <Label>{t('Transition outcomes')}</Label>
              <div className="space-y-2 mt-1">
                {qualifiable.map((tr) => (
                  <div key={tr.id} className="flex items-center justify-between gap-2">
                    <span className="text-sm truncate">{tr.name} ({tr.id})</span>
                    <Select
                      value={kinds[tr.id] ?? 'neutral'}
                      onValueChange={(v) => {
                        if (!v) return; // spurious native-select sync, see the desk select
                        setKinds((prev) => {
                          const next = { ...prev };
                          if (v === 'neutral') delete next[tr.id];
                          else next[tr.id] = v as DecisionKind;
                          return next;
                        });
                      }}
                    >
                      <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="neutral">{t('No email')}</SelectItem>
                        <SelectItem value="approve">{t('Approval')}</SelectItem>
                        <SelectItem value="reject">{t('Rejection')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {t('Only transitions qualified as approval or rejection send the decision email; the others stay silent.')}
              </p>
            </div>
          ) : null}

          {mutation.error ? (
            <p className="text-sm text-red-600">{(mutation.error as Error).message}</p>
          ) : null}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>{t('Cancel')}</Button>
            <Button type="submit" disabled={mutation.isPending || !serviceDeskId || !statusId || !name.trim()}>
              {mutation.isPending ? t('Saving…') : isEdit ? t('Save') : t('Create')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
